/**
 * Renderer selection (WGP-001b / AD-195) — the PURE, node-testable core of the
 * flag-gated dual-renderer decision. Binding spec:
 * docs/architecture/wgp1-webgpu-migration-standup.md §2 ("Renderer selection").
 *
 * Like quality.ts / platform.ts this module imports NO `three` and does no I/O,
 * so it runs under `node --test` with no renderer and no DOM. Keep it that way:
 * the async factory (`createGameRenderer`, WGP-001b steps 8-9) must live behind a
 * DYNAMIC `import()` so this file stays loadable by the dependency-free test
 * runner (see the handoff block at the end of this file).
 *
 * Decision (standup §2 "Rollout"; default flipped by the owner 2026-11-03, AD-9020 — the
 * O-072 hang that held it back was our sky shader, fixed 2026-10-18 by
 * `q-webgpu-sky-node-build-hang`):
 *   - default `webgpu` when BOTH hold:
 *       1. `navigator.gpu` exists — a synchronous capability probe, AND
 *       2. `await navigator.gpu.requestAdapter()` returned a non-null adapter;
 *   - otherwise the `webgl2` fallback — the AD-194 WebGLRenderer + EffectComposer path
 *     (AD-195 §5: a browser without WebGPU, or a blocklisted adapter, plays exactly as before);
 *   - `webgl2` regardless when OVERRIDDEN — `?renderer=webgl2` (a player's escape hatch) or
 *     `window.__GC_CONFIG__.renderer === 'webgl2'` (AD-124 runtime config: a deploy-wide kill
 *     switch, no rebuild). The URL wins over the config.
 *
 * Condition 2 is the only ASYNC input; the probe LIVES IN THE FACTORY, which
 * feeds its boolean result in as `adapterOk` so the whole policy stays pure and
 * unit-tested. A WebGPU init failure in the factory, and a Game that fails to start on
 * WebGPU (App), both fall back to WebGL2 as well.
 *
 * Presentation-only, off the per-tick path. Import boundary (AD-122):
 * frontend-local; no shared/server imports.
 */

import type { WebGLRenderer } from 'three';
import type { WebGPURenderer } from 'three/webgpu';

import type { FlameMaterialFactory } from './scene/flame.js';
import type { GroundCoverMaterialFactory } from './scene/groundCoverMesh.js';
import type { GroundMaterialFactory } from './scene/groundMaterial.js';
import type { NodePostFxFactory } from './scene/nodePostfx.js';
import type { SkyFactory } from './scene/sky.js';
import { effectiveSearch } from './devFlags.js';

/** The two supported renderer backends. */
export type RendererKind = 'webgl2' | 'webgpu';

/** The default backend wherever the browser offers a WebGPU adapter (AD-9020). */
export const DEFAULT_RENDERER_KIND: RendererKind = 'webgpu';
/** The fallback — the AD-194 WebGL2 path (AD-195 §5). */
export const FALLBACK_RENDERER_KIND: RendererKind = 'webgl2';

function asKind(value: string | null | undefined): RendererKind | null {
  return value === 'webgl2' || value === 'webgpu' ? value : null;
}

/**
 * Pure: the backend explicitly asked for, or `null` for none. The `?renderer=` search
 * param wins over the runtime config (`window.__GC_CONFIG__.renderer`, read by the
 * factory). The compare is exact/case-sensitive (matching quality.ts's `=== 'low'`
 * precedent); a malformed search string is treated as "no override".
 */
export function rendererOverride(
  search: string,
  configRenderer: string | undefined,
): RendererKind | null {
  let fromUrl: RendererKind | null = null;
  try {
    fromUrl = asKind(new URLSearchParams(search).get('renderer'));
  } catch {
    fromUrl = null;
  }
  return fromUrl ?? asKind(configRenderer);
}

/** Inputs to the pure renderer-kind decision — the factory gathers these. */
export interface RendererSelectionInputs {
  /** The URL search string (e.g. `location.search`); '' when unavailable. */
  readonly search: string;
  /** `window.__GC_CONFIG__.renderer`, or `undefined` when unset (AD-124). */
  readonly configRenderer: string | undefined;
  /** Whether `navigator.gpu` exists — the synchronous capability probe. */
  readonly gpuPresent: boolean;
  /**
   * Whether `await navigator.gpu.requestAdapter()` returned a non-null adapter.
   * The FACTORY performs this async probe (skipped when `webgl2` is forced or
   * `gpuPresent` is false) and passes the result; `false` when not probed.
   */
  readonly adapterOk: boolean;
}

/**
 * Resolve the renderer backend from the selection inputs (standup §2, AD-9020). Pure:
 * same inputs → same kind. `webgl2` when overridden to it; otherwise `webgpu` when
 * `navigator.gpu` is present AND the adapter probe succeeded; otherwise the `webgl2`
 * fallback (AD-195 §5).
 */
export function resolveRendererKind(inputs: RendererSelectionInputs): RendererKind {
  if (rendererOverride(inputs.search, inputs.configRenderer) === FALLBACK_RENDERER_KIND) {
    return FALLBACK_RENDERER_KIND;
  }
  if (inputs.gpuPresent && inputs.adapterOk) return DEFAULT_RENDERER_KIND;
  return FALLBACK_RENDERER_KIND;
}

/** The renderer surface accepted by the game and asset-loader seams. */
export type GameRenderer = WebGLRenderer | WebGPURenderer;

/**
 * The factory result is discriminated so Game can construct the concrete WebGL
 * EffectComposer without casts while the WebGPU branch receives its dynamically
 * loaded node-stack factory. Keeping nodePostfx behind this result prevents the
 * opt-in WebGPU implementation from entering the default WebGL2 bundle.
 */
export type CreatedRenderer =
  | {
      readonly kind: 'webgl2';
      readonly renderer: WebGLRenderer;
    }
  | {
      readonly kind: 'webgpu';
      readonly renderer: WebGPURenderer;
      readonly createPostFx: NodePostFxFactory;
      /** The WebGPU ground-material sibling (groundNodeMaterial.ts; AD-651 amendment). */
      readonly createGroundMaterial: GroundMaterialFactory;
      /** The WebGPU sky-dome sibling (skyNodeMesh.ts — three's TSL SkyMesh; AD-651 item 2). */
      readonly createSky: SkyFactory;
      /** The WebGPU flame material (flameNodeMaterial.ts; q-npc-props-adoption-and-flame). */
      readonly createFlameMaterial: FlameMaterialFactory;
      /** The WebGPU ground-cover sibling (groundCoverNodeMaterial.ts; q-webgpu-grass-and-flame-parity). */
      readonly createGroundCover: GroundCoverMaterialFactory;
    };

/**
 * Reclaim a renderer that App created but could not hand to a live Game.
 * Cleanup is deliberately best-effort so a failing WebGL context-loss hook or
 * renderer disposal cannot prevent the canvas from being detached.
 */
export function disposeCreatedRenderer(created: CreatedRenderer): void {
  const renderer = created.renderer;
  const maybeWebGl = renderer as { forceContextLoss?: () => void };
  try {
    maybeWebGl.forceContextLoss?.();
  } catch {
    /* context already lost */
  }
  try {
    renderer.dispose();
  } catch {
    /* partial renderer initialization may make disposal fail */
  }
  try {
    renderer.domElement.remove();
  } catch {
    /* canvas was never attached, or is already detached */
  }
}

/** What the client knows about a renderer that died after it started. */
export interface RendererLossInfo {
  readonly kind: RendererKind;
  readonly message: string;
}

/** The slice of a canvas the WebGL2 watch touches — a DOM canvas satisfies it. */
interface LossEventTarget {
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

/** three's WebGPU hook: `device.lost` (reason ≠ 'destroyed') calls this. */
interface DeviceLostHook {
  onDeviceLost: (info: { message?: string; reason?: string | null }) => void;
}

/**
 * q-no-device-lost-recovery-either-renderer: the fail-safe in
 * `createGameRenderer()` is INIT-TIME ONLY — a renderer that dies after it
 * returned used to leave a black canvas, a render loop driving a dead context,
 * and no word to the player. This is the one runtime seam, per path:
 *
 * - WebGL2 (the fallback): `webglcontextlost` on the canvas,
 *   with `preventDefault()` — without it the browser never offers restoration.
 * - WebGPU: three's `renderer.onDeviceLost` hook (fired from `device.lost`,
 *   already skipping an intentional `destroy()`); three's own default still runs
 *   first, so its console line and `_isDeviceLost` flag are unchanged.
 *
 * `onLost` fires at most once. The returned function detaches the watch and
 * MUST run before a deliberate teardown: `forceContextLoss()` in `dispose()`
 * raises the very event this listens for.
 *
 * Deliberately NOT done (AD-269): re-initialising a renderer, or falling back to
 * WebGL2 after a WebGPU loss. A hung GPU may take the fallback down with it; a
 * reload prompt is the weaker, safer commitment.
 */
export function watchRendererLoss(
  created: CreatedRenderer,
  onLost: (info: RendererLossInfo) => void,
): () => void {
  let fired = false;
  const report = (message: string): void => {
    if (fired) return;
    fired = true;
    console.error(`[renderer] ${created.kind} renderer lost: ${message}`);
    onLost({ kind: created.kind, message });
  };

  if (created.kind === 'webgl2') {
    const canvas = created.renderer.domElement as unknown as LossEventTarget;
    const listener = (event: Event): void => {
      event.preventDefault();
      report('WebGL context lost');
    };
    canvas.addEventListener('webglcontextlost', listener);
    return () => canvas.removeEventListener('webglcontextlost', listener);
  }

  const hook = created.renderer as unknown as DeviceLostHook;
  const previous = hook.onDeviceLost;
  const watched: DeviceLostHook['onDeviceLost'] = (info) => {
    try {
      previous?.call(hook, info);
    } finally {
      report(info.message || 'WebGPU device lost');
    }
  };
  hook.onDeviceLost = watched;
  return () => {
    if (hook.onDeviceLost === watched) hook.onDeviceLost = previous;
  };
}

interface GpuFacade {
  requestAdapter(): Promise<unknown | null>;
}

function browserGpu(): GpuFacade | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { gpu?: GpuFacade }).gpu ?? null;
}

function runtimeRendererConfig(): string | undefined {
  return (globalThis as { __GC_CONFIG__?: { renderer?: string } }).__GC_CONFIG__?.renderer;
}

function runtimeSearch(): string {
  // O-026: the EFFECTIVE search — the URL overlaid with the playtest panel's
  // persisted overrides (a URL param still wins). Production builds get
  // `window.location.search` verbatim, so deployed behaviour is unchanged.
  return effectiveSearch();
}

function pixelRatio(): number {
  try {
    return Math.min(window.devicePixelRatio, 2);
  } catch {
    return 1;
  }
}

async function createWebGlRenderer(): Promise<CreatedRenderer> {
  const THREE = await import('three');
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(pixelRatio());
  return { kind: 'webgl2', renderer };
}

/**
 * Build the active renderer after the override check + adapter probe. WebGPU is
 * fail-safe: an unavailable adapter, module/init failure, or partial device
 * setup falls back to the WebGL2 renderer instead of blanking the client.
 * `forceWebGl` (App's retry after a Game that failed to start on WebGPU) skips
 * the probe. Both renderers and the node post-fx stay dynamically imported, so a
 * WebGL2 browser never downloads the WebGPU bundle.
 */
export async function createGameRenderer(
  options: { forceWebGl?: boolean } = {},
): Promise<CreatedRenderer> {
  const search =
    options.forceWebGl === true ? `?renderer=${FALLBACK_RENDERER_KIND}` : runtimeSearch();
  const configRenderer = runtimeRendererConfig();
  const gpu = browserGpu();
  let adapterOk = false;

  if (rendererOverride(search, configRenderer) !== FALLBACK_RENDERER_KIND && gpu !== null) {
    try {
      adapterOk = (await gpu.requestAdapter()) !== null;
    } catch {
      adapterOk = false;
    }
  }

  const requestedKind = resolveRendererKind({
    search,
    configRenderer,
    gpuPresent: gpu !== null,
    adapterOk,
  });

  if (requestedKind === 'webgpu') {
    let renderer: WebGPURenderer | null = null;
    try {
      const [
        { WebGPURenderer },
        { createNodePostProcessingStack },
        { createGroundNodeMaterial },
        { createSkyNodeMesh },
        { createFlameNodeMaterial },
        { createGroundCoverNodeMaterial },
      ] = await Promise.all([
        import('three/webgpu'),
        import('./scene/nodePostfx.js'),
        import('./scene/groundNodeMaterial.js'),
        import('./scene/skyNodeMesh.js'),
        import('./scene/flameNodeMaterial.js'),
        import('./scene/groundCoverNodeMaterial.js'),
      ]);
      renderer = new WebGPURenderer({ antialias: true });
      await renderer.init();
      renderer.setPixelRatio(pixelRatio());
      console.info('[renderer] active backend: webgpu');
      return {
        kind: 'webgpu',
        renderer,
        createPostFx: createNodePostProcessingStack,
        createGroundMaterial: createGroundNodeMaterial,
        createSky: createSkyNodeMesh,
        createFlameMaterial: createFlameNodeMaterial,
        createGroundCover: createGroundCoverNodeMaterial,
      };
    } catch (error) {
      renderer?.dispose();
      console.warn('[renderer] WebGPU initialization failed; falling back to WebGL2', error);
    }
  }

  const fallback = await createWebGlRenderer();
  console.info('[renderer] active backend: webgl2');
  return fallback;
}
